import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { refreshedClaim, reviewGrounding, reviewSections } from '../../stores/slices/reviewsSlice';
import type { ThreadContext } from '../../apis';
import type { ConversationThread } from '../../types';

/**
 * The scope a question asked from a review starts with (#568): the review's
 * first perspective, the catalog metrics pinned in it, the window its numbers
 * share. The answer opens as its own thread, stamped with the review.
 */
export function reviewScope(thread: ConversationThread): ThreadContext | null {
  const perspective = reviewGrounding(thread)[0] || null;
  if (!perspective) return null;
  const grounded = reviewSections(thread).filter((s) => s.kind === 'claim')
    .map((s) => refreshedClaim(s).grounding).filter((g) => g?.perspective === perspective && g?.metric_qid);
  const metrics = Array.from(new Set(grounded.map((g) => g!.metric_qid as string))).slice(0, 12);
  const w = grounded.find((g) => g!.window?.start && g!.window?.end)?.window || null;
  return { perspective, metrics, primary_metric: null, dimensions: [], filters: [], definitions: [], session_id: null,
    window: w ? { start: w.start, end: w.end } : null };
}

export default function AskThisReview({ thread }: { thread: ConversationThread }) {
  const [text, setText] = useState('');
  const [refused, setRefused] = useState(false);
  const scope = reviewScope(thread);
  const ask = () => {
    const st = useVoyagerStore.getState();
    if (!scope || !text.trim()) return;
    // asked in the review's perspective, for this question only (#767: the pick is never switched for the asker)
    const started = st.sendLandingAgentQuery(text, { source: { kind: 'review', id: thread.id }, threadContext: scope,
      ...(scope.perspective ? { perspectiveId: scope.perspective } : {}) });
    setRefused(!started);
    if (started) setText('');
  };
  return (
    <Box data-testid="review-ask" sx={{ display: 'grid', gap: 0.75 }}>
      <TextField size="small" multiline maxRows={4} value={text} placeholder="Ask about this review's numbers…" disabled={!scope}
        onChange={(e) => setText(e.target.value)} inputProps={{ 'aria-label': 'Ask this review', 'data-testid': 'review-ask-input' }}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent as any).isComposing) { e.preventDefault(); ask(); } }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary', lineHeight: 1.4 }}>
          {scope ? `Asked in a new thread, scoped to ${scope.metrics.length ? `this review's ${scope.metrics.length} number${scope.metrics.length === 1 ? '' : 's'}` : 'its grounding'}${scope.window ? ` over ${scope.window.start} → ${scope.window.end}` : ''}` : 'This review has no grounding to ask in'}
        </Typography>
        <Button size="small" variant="contained" disabled={!scope || !text.trim()} onClick={ask} data-testid="review-ask-send">Ask</Button>
      </Box>
      {refused && <Typography variant="caption" color="error" data-testid="review-ask-refused">The question did not start — try again</Typography>}
    </Box>
  );
}
